'use client';

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ContactInquiryPanel from './ContactInquiryPanel';
import ContactInfoItem from './ContactInfoItem';
import type { ContactFormSection as ContactFormSectionType } from '@/types/contact';
import type { LeadForm } from '@/types/lead-forms';

interface ContactFormSectionProps {
  section: ContactFormSectionType;
  leadForms: LeadForm[];
}

export default function ContactFormSection({ section, leadForms }: ContactFormSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const contactInfo = section.contactInfo;

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.ct-form-col',
        { autoAlpha: 0, y: 40 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.85,
          ease: 'power3.out',
          scrollTrigger: { trigger: '.ct-form-col', start: 'top 88%', once: true },
        },
      );
      gsap.fromTo(
        '.ct-info-col',
        { autoAlpha: 0, y: 40 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.85,
          ease: 'power3.out',
          delay: 0.12,
          scrollTrigger: { trigger: '.ct-info-col', start: 'top 88%', once: true },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={sectionRef} className="bg-[#f3f3f3]">
      <div className="mx-auto container max-sm:px-4">
        <div className="flex flex-col items-stretch gap-6 lg:flex-row lg:gap-7">
          <div className="ct-form-col w-full lg:flex-[3]">
            <ContactInquiryPanel leadForms={leadForms} />
          </div>

          {contactInfo ? (
            <div className="ct-info-col flex w-full flex-col lg:flex-[2]">
              <div className="flex h-full flex-col rounded-2xl bg-white px-6 py-6 shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
                {contactInfo.eyebrowHeading ? (
                  <p className="mb-1 text-[9.5px] font-bold uppercase tracking-[0.28em] text-[#E31E24]">
                    {contactInfo.eyebrowHeading}
                  </p>
                ) : null}
                {contactInfo.heading ? (
                  <p className="mb-6 font-figtree text-[1.25rem] font-semibold leading-tight text-stone-900">
                    {contactInfo.heading}
                  </p>
                ) : null}

                <div className="space-y-5">
                  {(contactInfo.contactItem ?? []).map((item) => (
                    <ContactInfoItem key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
